import { todayKey } from './game';

// localStorage-backed daily progress + streak, namespaced per mode (best-practice §8.1 A).

const progressKey = (mode: string) => `animedle.${mode}.progress`;
const streakKey = (mode: string) => `animedle.${mode}.streak`;

export interface DayProgress {
  day: string; // todayKey
  guessIds: number[];
  status: 'playing' | 'won' | 'lost';
}

export function loadProgress(mode: string): DayProgress {
  try {
    const raw = localStorage.getItem(progressKey(mode));
    if (raw) {
      const p = JSON.parse(raw) as DayProgress;
      if (p.day === todayKey()) return p;
    }
  } catch {
    /* ignore */
  }
  return { day: todayKey(), guessIds: [], status: 'playing' };
}

export function saveProgress(mode: string, p: DayProgress): void {
  try {
    localStorage.setItem(progressKey(mode), JSON.stringify(p));
  } catch {
    /* ignore */
  }
}

interface StreakState {
  current: number;
  best: number;
  lastWonDay: string | null;
}

function loadStreak(mode: string): StreakState {
  try {
    const raw = localStorage.getItem(streakKey(mode));
    if (raw) return JSON.parse(raw) as StreakState;
  } catch {
    /* ignore */
  }
  return { current: 0, best: 0, lastWonDay: null };
}

function yesterdayKey(): string {
  const d = new Date();
  d.setUTCDate(d.getUTCDate() - 1);
  return d.toISOString().slice(0, 10);
}

// Call once when the player wins today. Idempotent for the same day.
export function recordWin(mode: string): StreakState {
  const s = loadStreak(mode);
  const today = todayKey();
  if (s.lastWonDay === today) return s;
  s.current = s.lastWonDay === yesterdayKey() ? s.current + 1 : 1;
  s.best = Math.max(s.best, s.current);
  s.lastWonDay = today;
  try {
    localStorage.setItem(streakKey(mode), JSON.stringify(s));
  } catch {
    /* ignore */
  }
  return s;
}

export function currentStreak(mode: string): number {
  const s = loadStreak(mode);
  if (s.lastWonDay === todayKey() || s.lastWonDay === yesterdayKey()) return s.current;
  return 0;
}

// Today's status for a mode, for the cross-mode progress display (best-practice §2.3).
export function modeStatus(mode: string): 'won' | 'lost' | 'playing' | 'none' {
  const p = loadProgress(mode);
  if (p.guessIds.length === 0 && p.status === 'playing') return 'none';
  return p.status;
}
